// Globals
import { type PromptProps } from '@ant-design/x';
import { Button, GetProps } from 'antd';
import { useEffect, useMemo, useRef } from 'react';

// Modules
import { useSocket } from '@/modules/Socket';

// Components
import { ClearLogoIcon, CloseOutlineIcon } from '@/icons';
import { Message } from '../../components';
import { SenderContainer } from '../SenderContainer/SenderContainer';
import UserFormContainer from '../UserFormContainer/UserFormContainer';
import {
  ContentScrollArea,
  SenderStickyFooter,
  StyledBlock,
  StyledBubbleList,
  StyledClearTitle,
  StyledClearWrap,
  StyledContainer,
  StyledInfo,
  StyledName,
  StyledPrompts,
  StyledTime,
  StyledWrapper,
  VerticalDivider
} from './ChatContainerStyles';

// Helpers
import { formatDate, formatTime } from '@/utils';
import { useBoolean } from '@/hooks';

type TChatContainerWrapperProps = {
  onClose: () => void;
}

type BubbleDataType = NonNullable<GetProps<typeof StyledBubbleList>['items']>[number];

const ChatContainerWrapper = ({ onClose }: TChatContainerWrapperProps) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { value: isClearAvailable, toggle } = useBoolean();

  const {
    sendMessage,
    setMessages,
    messages,
    buttons,
    isTyping,
    formFields,
    sendForm,
    clearForm,
  } = useSocket();
  const isFormExist = formFields?.length > 0;

  useEffect(() => {
    (messagesEndRef.current as HTMLElement)?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handlePressButton = async (value: { data: PromptProps & { action?: string } }) => {
    if (!value?.data?.label) return;
    sendMessage(value.data.label.toString());
  }

  const items = useMemo(() => {
    const items: BubbleDataType[] = messages.map((item) => ({
      key: item?.message?.toString(),
      content: <Message message={item.message?.toString() ?? ''} />,
      placement: item.sender === 'USER' ? 'end' as const : 'start' as const,
      loading: false,
      footer: item.date && <StyledTime dateTime={formatDate(item.date)}>{formatTime(item.date)}</StyledTime>
    }));

    return isTyping ? items.concat([{
      key: 'isTyping',
      content: '...' as const,
      loading: true,
      placement: 'start' as const
    }]) : items;
  }, [messages, isTyping]);

  const suggestions: PromptProps[] = useMemo(() => (buttons ?? []).map((item) => ({
    label: item.text,
    key: item.text,
    action: item.action,
  })), [buttons]);

  const handleSendMessage = (message: string) => {
    sendMessage(message);

    setTimeout(() => {
      (messagesEndRef.current as HTMLElement)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleClear = () => {
    toggle();
    clearForm();
    setMessages([]);
  };

  const renderClearForm = () => {
    return (
      <StyledClearWrap>
        {isClearAvailable ? 
          <>
            <StyledClearTitle onClick={handleClear}>Yes, I want to clear the form</StyledClearTitle>
            <VerticalDivider />
            <StyledClearTitle onClick={toggle}>No</StyledClearTitle>
          </>
        :
          <StyledClearTitle onClick={toggle}>Clear form</StyledClearTitle>
        }
      </StyledClearWrap>
    );
  };

  return (
    <StyledWrapper>
      <StyledContainer
        title={
          <StyledBlock>
            <ClearLogoIcon />
            <div>
              <StyledName>Purehelper</StyledName>
              <StyledInfo>Online</StyledInfo>
            </div>
          </StyledBlock>
        }
        type="inner"
        extra={<Button size="large" ghost icon={<CloseOutlineIcon />} onClick={onClose} />}
      >
        <ContentScrollArea>
          <StyledBubbleList items={items} />
          {!isTyping && suggestions?.length > 0 && formFields?.length <= 0 && (
            <StyledPrompts
              wrap
              items={suggestions}
              onItemClick={handlePressButton}
            />
          )}

          {isFormExist && (
            <UserFormContainer
              formFields={formFields}
              handleSubmit={sendForm}
            />
          )}

          <div ref={messagesEndRef} />
        </ContentScrollArea>

        <SenderStickyFooter>
          <SenderContainer
            onSend={handleSendMessage}
            disabled={isFormExist}
          />
          {renderClearForm()}
        </SenderStickyFooter>
      </StyledContainer >
    </StyledWrapper>
  );
};

export default ChatContainerWrapper;
